import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  theme,
  useToast,
} from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import { useSession } from 'src/apps/invest/common/hooks/useSession';
import { useMyProfileModel } from '@common/models/MyProfileModel';
import storage from '@common/storage';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { fetchAuthSession } from 'aws-amplify/auth';

type AvatarUploaderProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const AvatarUploader: React.FC<AvatarUploaderProps> = ({ isOpen, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File>();
  const [preview, setPreview] = useState<string>();
  const [state, setState] = useState<'LOADING' | 'RENDER' | 'ERROR'>('RENDER');
  const [error, setError] = useState<string>();
  const session = useSession().session;
  const toast = useToast();
  const myProfileModel = useMyProfileModel();

  async function handleSave() {
    setState('LOADING');
    if (!selectedFile) return;
    const result = await storage.saveHeadshot(selectedFile);
    if (result.error) {
      setState('ERROR');
      setError(result.error);
      return;
    }
    onClose();
    toast({
      description: `Your avatar has been updated.`,
      duration: 5000,
      isClosable: true,
      position: 'top',
      status: 'info',
      title: `Saved`,
    });
    const amplifySession = await fetchAuthSession();
    if (!amplifySession.userSub) {
      // No user sub found. This should not happen.
      return;
    }
    myProfileModel.fetchMyProfile(amplifySession.userSub);
    setState('RENDER');
  }

  function onSelectFile(e: ChangeEvent<HTMLInputElement>) {
    if (!e.target.files || e.target.files.length === 0) return setSelectedFile(undefined);
    setSelectedFile(e.target.files[0]);
    setError(undefined);
  }

  useEffect(() => {
    if (!selectedFile) return setPreview(undefined);
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  return (
    <Modal
      blockScrollOnMount={false}
      isOpen={isOpen}
      onClose={state === 'LOADING' ? () => null : onClose}
      closeOnEsc={state === 'LOADING' ? false : true}
      isCentered={true}>
      <ModalOverlay />
      <ModalContent>
        {state !== 'LOADING' && <ModalHeader></ModalHeader>}
        <ModalCloseButton />
        {state === 'LOADING' && (
          <ModalBody display="flex" alignItems={'center'} justifyContent={'center'} padding="50px">
            <Spinner color="blue.500" />
          </ModalBody>
        )}
        {state === 'ERROR' && (
          <ModalBody display="flex" alignItems={'center'} justifyContent={'center'} padding="50px">
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </ModalBody>
        )}
        {state === 'RENDER' && (
          <ModalBody
            display="flex"
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'center'}
            paddingBottom={'40px'}
            gap="20px"
            opacity={0.7}
            transition="all 0.2s"
            pos={'relative'}
            _hover={{
              opacity: 1,
            }}>
            <Input
              cursor={'pointer'}
              type="file"
              height="100%"
              width="100%"
              position="absolute"
              opacity={0}
              top="0"
              left="0"
              aria-hidden="true"
              accept="image/*"
              zIndex={1}
              onChange={onSelectFile}
            />
            <Box position={'relative'}>
              <Avatar
                border={`3px solid ${theme.colors.white}`}
                cursor={'pointer'}
                name={session?.name}
                src={selectedFile ? preview : myProfileModel.myProfile?.headshot?.download_url ?? ''}
                padding={'3px'}
                boxSize="175px"
                _hover={{
                  border: `3px solid ${theme.colors.blue[500]}`,
                  cursor: 'pointer',
                }}
              />
              {!selectedFile && (
                <Icon
                  icon="image"
                  variant="regular"
                  position="absolute"
                  left={'calc(50% - 15px)'}
                  top={'calc(50% - 15px)'}
                  color="white"
                  boxSize={'30px'}
                  pointerEvents={'none'}
                />
              )}
            </Box>
            <TextBlock>
              Drop your image here or{' '}
              <TextBlock as="span" color="blue.500" weight="bold">
                click to upload
              </TextBlock>
            </TextBlock>
          </ModalBody>
        )}

        {state !== 'LOADING' && (
          <ModalFooter display={'flex'} borderTop={`1px solid ${theme.colors.gray[200]}`} gap="20px">
            <Button variant="ghost" onClick={onClose}>
              {state === 'ERROR' ? 'Close' : 'Cancel'}
            </Button>
            {state === 'RENDER' && (
              <Button onClick={handleSave} isDisabled={!preview}>
                {'Save'}
              </Button>
            )}
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};
